import { BrandLoader } from "@/components/brand";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Link2 } from "lucide-react";
import { useCallback, useEffect, useState, type FC } from "react";

import { CasePrepUscisFormsAPI } from "../../casePrepUscisForms/api";
import { FilledFormDetail } from "../../casePrepUscisForms/components/CasePrepUscisFormsPanel";
import type { CasePrepUscisFilledForm } from "../../casePrepUscisForms/types";
import { artifactKey } from "../types";
import { useCaseIde, useOnInvalidate } from "../context/CaseIdeContext";
import { AddToCaseFilesDialog } from "../files/AddToCaseFilesDialog";
import { formCopySource, type CopySource } from "../files/caseFileCopies";

type Props = {
  formId: string;
};

/**
 * A USCIS form open in the center pane: the existing filled-form editor,
 * loaded by id so it does not need the forms list context. Its overflow menu
 * carries Add to Case Files….
 */
export const FormPane: FC<Props> = ({ formId }) => {
  const { individualId, close, invalidate } = useCaseIde();
  const [form, setForm] = useState<CasePrepUscisFilledForm | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [promoteSource, setPromoteSource] = useState<CopySource | null>(null);

  const load = useCallback(async () => {
    try {
      setForm(await CasePrepUscisFormsAPI.getFilledForm(individualId, formId));
      setError(null);
    } catch {
      setError("Couldn't load this form.");
    }
  }, [formId, individualId]);

  useEffect(() => {
    setForm(null);
    void load();
  }, [load]);
  useOnInvalidate(["forms"], load);

  if (error) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        {error}
      </div>
    );
  }
  if (!form) {
    return (
      <div className="flex h-full items-center justify-center">
        <BrandLoader label="Loading form" />
      </div>
    );
  }
  return (
    <>
      <FilledFormDetail
        individualId={individualId}
        form={form}
        onBack={() => close(artifactKey({ kind: "form", id: formId }))}
        onUpdated={(next) => {
          setForm(next);
          // A fill's polling hands back the same form until it moves.
          if (
            next.status !== form.status ||
            next.updated_at !== form.updated_at
          ) {
            invalidate(["forms"]);
          }
        }}
        moreMenuItems={
          <DropdownMenuItem
            disabled={form.status !== "generated"}
            onSelect={() => setPromoteSource(formCopySource(form))}
          >
            <Link2 />
            Add to Case Files…
          </DropdownMenuItem>
        }
      />
      <AddToCaseFilesDialog
        source={promoteSource}
        onOpenChange={(o) => !o && setPromoteSource(null)}
      />
    </>
  );
};
